
import {
    Container, Divider, Grid, Typography
} from "@material-ui/core";
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles';
import {
    BackspaceOutlined as BackspaceOutlinedIcon,
    CloudDownload, Search as SearchIcon
} from "@material-ui/icons";
import clsx from "clsx";
import MUIDataTable from "mui-datatables";
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useState } from "react";
import { useForm } from 'react-hook-form';
import { useTranslation } from "react-i18next";
import { useHistory } from 'react-router-dom';
import TextField from '../../components/Form/TextField';
import SelectPartNumber from '../../components/Form/SelectPartNumber';
import ButtonUI from "../../components/ui/ButtonUI";
import LoadingUI from "../../components/ui/LoadingUI";
import PageTitle from "../../components/ui/PageTitleUI";
import { useAuth } from '../../hooks/auth';
import { getCompanyIdsByPlantId, getCompanyNameById } from '../../interfaces/Companys';
import MaterialUnloadShedApi from "../../services/MaterialUnloadShedApi";
import getDefaultGridOptions from '../../utils/componentUtils';
import permissions from "../../utils/Permissions";
import exportExcelFileFrom from '../../utils/xlsxExport';
import { useStyles } from "./Style";

interface IFormInput {
    companyId: any;
    selectedpartnumber: any[];
    shedCode: any;
}

const MaterialUnloadShed: React.FC = () => {
    const classes = useStyles();
    const history = useHistory();
    const [materialUnloadSheds, setMaterialUnloadSheds] = useState<any>();
    const [loading, setLoading] = useState(true);
    const { user, hasPermission } = useAuth();
    const { t } = useTranslation("MaterialUnloadShed");
    const { t: _t } = useTranslation("General");
    const { enqueueSnackbar } = useSnackbar();
    const { control, handleSubmit, reset, getValues } = useForm<IFormInput>(
        {
            defaultValues: { companyId: '', selectedpartnumber: [], shedCode: '' }
        }
    );
    const [page, setPage] = useState(0);
    const [count, setCount] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [submitData, setSubmitData] = useState<any>(null);

    const searchMaterialUnloadSheds = useCallback((data: any) => {
        setLoading(true);
        const searchParams = {
            companyIds: getCompanyIdsByPlantId(Number(user.company.plantID)),
            partNumberCodes: data.selectedpartnumber?.map((x: any) => x.partnumberCode).filter(Boolean),
            shedCode: data.shedCode || undefined,
            pageSize: rowsPerPage,
            pageIndex: page,
        };
        MaterialUnloadShedApi.searchMaterialUnloadShed(searchParams)
            .then((result) => {
                if (result && result.items) {
                    setMaterialUnloadSheds(result.items);
                    setCount(result.totalCount);
                    setPage(result.pageIndex);
                } else {
                    setMaterialUnloadSheds([]);
                    setCount(0);
                }
            })
            .catch(() => {
                setMaterialUnloadSheds([]);
                setCount(0);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [page, rowsPerPage]);

    useEffect(() => {
        if (!hasPermission(permissions.MATERIAL_UNLOAD_SHED.Search))
            history.replace('NoPermission');
    }, [history, user]);

    useEffect(() => {
        setMaterialUnloadSheds([]);
        const data = getValues();
        setSubmitData(data);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (submitData === null || submitData === undefined) return;
        searchMaterialUnloadSheds(submitData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitData, page, rowsPerPage]);

    const onSubmit = (data: any) => {
        setPage(0);
        setSubmitData(data);
    };

    const handleExportExcel = () => {
        const exportParams = {
            companyIds: getCompanyIdsByPlantId(Number(user.company.plantID)),
            partNumberId: undefined,
            shedCode: undefined,
            pageSize: 999999,
            pageIndex: 0,
        };
        MaterialUnloadShedApi.searchMaterialUnloadShed(exportParams)
            .then((result) => {
                if (result && result.items && result.items.length > 0) {
                    const dataExport = result.items.map((item: any) => ({
                        [t("companyId")]: getCompanyNameById(item.companyId),
                        [t("partNumberCode")]: item.partNumberCode,
                        [t("partNumberDescription")]: item.partNumberDescription,
                        [t("shedCode")]: item.shedCode,
                    }));
                    exportExcelFileFrom(dataExport, null, "material-unload-shed");
                } else {
                    enqueueSnackbar(t("noDataToExport"), { variant: "warning" });
                }
            })
            .catch(() => {
                enqueueSnackbar(t("exportError"), { variant: "error" });
            });
    };

    const centerTableHead = clsx({
        [classes.tableHeadCenter]: true,
    });

    const columns = [
        {
            name: "companyId",
            label: String(t("companyId")),
            options: {
                sort: false,
                setCellHeaderProps: () => ({
                    className: centerTableHead,
                }),
                setCellProps: () => ({
                    align: "center",
                }),
                customBodyRender: (value: any) => getCompanyNameById(value),
            },
        },
        {
            name: "partNumberCode",
            label: String(t("partNumberCode")),
            options: {
                sort: false,
                setCellHeaderProps: () => ({
                    className: centerTableHead,
                }),
                setCellProps: () => ({
                    align: "center",
                }),
            },
        },
        {
            name: "partNumberDescription",
            label: String(t("partNumberDescription")),
            options: {
                sort: false,
                setCellHeaderProps: () => ({
                    className: centerTableHead,
                }),
                setCellProps: () => ({
                    align: "center",
                }),
            },
        },
        {
            name: "shedCode",
            label: String(t("shedCode")),
            options: {
                sort: false,
                setCellProps: () => ({
                    align: "center",
                }),
            },
        },
    ];

    const myTheme = createTheme({
        overrides: {
            MuiTableBody: {
                root: {
                    cursor: ''
                }
            }
        }
    });

    return (
        <Container maxWidth={false} component="main">
            <PageTitle title={t("materialUnloadShed")} />
            <div className={classes.rootContent}>
                <div className={classes.paper}>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={1}>
                            <Grid
                                item
                                xs={6}
                                style={{
                                    padding: "0px",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                <Typography className={classes.root}>
                                    {t("titleSearch")}
                                </Typography>
                            </Grid>

                            <Grid
                                item
                                xs={6}
                                style={{ padding: "0px", textAlign: "right" }}
                            >
                                <ButtonUI
                                    onClick={() => reset()}
                                    startIcon={<BackspaceOutlinedIcon />}
                                >
                                    {t("clean")}
                                </ButtonUI>
                                <ButtonUI
                                    type="submit"
                                    color="primary"
                                    startIcon={<SearchIcon />}
                                >
                                    {t("search")}
                                </ButtonUI>
                                <ButtonUI
                                    color="primary"
                                    onClick={handleExportExcel}
                                    disabled={!hasPermission(permissions.MATERIAL_UNLOAD_SHED.Search)}
                                    startIcon={<CloudDownload />}
                                >
                                    {_t("exportExcel")}
                                </ButtonUI>
                            </Grid>
                        </Grid>

                        <Divider style={{ marginTop: "10px" }} />

                        <Grid container spacing={1}>
                            <Grid item xs={3}>
                                <SelectPartNumber
                                    label={t("partNumberCode")}
                                    name="selectedpartnumber"
                                    control={control}
                                    multiple={true}
                                    groupItens={true}
                                    companyBOMCode=""
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField
                                    label={t("shedCode")}
                                    name="shedCode"
                                    control={control}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </form>

                    {loading ? (
                        <LoadingUI />
                    ) : (
                        <MuiThemeProvider theme={myTheme}>
                            <MUIDataTable
                                title=""
                                data={materialUnloadSheds}
                                columns={columns}
                                options={{
                                    ...getDefaultGridOptions(_t),
                                    serverSide: true,
                                    pagination: true,
                                    sort: materialUnloadSheds?.length ? true : false,
                                    selectToolbarPlacement: "none",
                                    filter: false,
                                    search: false,
                                    download: false,
                                    print: false,
                                    viewColumns: false,
                                    selectableRows: "none",
                                    page,
                                    count,
                                    rowsPerPage,
                                    onChangePage: (pageIndex) => { setPage(pageIndex); },
                                    onChangeRowsPerPage: (newRowsPerPage) => { setPage(0); setRowsPerPage(newRowsPerPage); },
                                }}
                            />
                        </MuiThemeProvider>
                    )}
                </div>
            </div>
        </Container>
    );
};

export default MaterialUnloadShed;
